import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { AuthActionResponse, User } from '../types';
import { authAPI, storeTokens, clearTokens, getStoredToken } from '../services/api';

interface RegisterData {
  email: string;
  password: string;
  username?: string;
  phone?: string;
  real_name: string;
  organization_name?: string;
  tenant_id?: string;
  create_new_tenant?: boolean;
  invite_code?: string;
  tenant_invitation_token?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ daily_login_reward?: number }>;
  register: (data: RegisterData) => Promise<AuthActionResponse>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => ({}),
  register: async () => ({ message: '' }),
  logout: async () => {},
  refreshUser: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!getStoredToken()) { setLoading(false); return; }
    try {
      const u = await authAPI.me();
      setUser(u);
    } catch {
      clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email: string, password: string) => {
    const tokens = await authAPI.login({ email, password });
    storeTokens(tokens.access_token, tokens.refresh_token);
    const u = await authAPI.me();
    setUser(u);
    return { daily_login_reward: tokens.daily_login_reward };
  };

  const register = async (data: RegisterData) => {
    return authAPI.register(data);
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch { /* ignore */ }
    clearTokens();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const u = await authAPI.me();
      setUser(u);
    } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
